import React, { useState, useEffect, Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { apiClient } from '../services/apiClient';

const Statistics = () => {
  const [selectedClinic, setSelectedClinic] = useState('all');
  const [selectedClinicDaily, setSelectedClinicDaily] = useState('all');
  const [selectedClinicMonthly, setSelectedClinicMonthly] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedYearMonthly, setSelectedYearMonthly] = useState(new Date().getFullYear());
  const [selectedYearByClinic, setSelectedYearByClinic] = useState(new Date().getFullYear());
  const [clinics, setClinics] = useState([]);
  const [dailyStats, setDailyStats] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [isDailyLoading, setIsDailyLoading] = useState(true);
  const [isMonthlyLoading, setIsMonthlyLoading] = useState(true);
  const [dailyError, setDailyError] = useState(null);
  const [monthlyError, setMonthlyError] = useState(null);
  const [byClinicData, setByClinicData] = useState([]);
  const [isByClinicLoading, setIsByClinicLoading] = useState(false);
  const [byClinicError, setByClinicError] = useState(null);

  // Function to format date for X-axis
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Function to format month for X-axis
  const formatMonth = (month) => {
    return new Date(2000, month - 1, 1).toLocaleDateString('fr-FR', { month: 'long' });
  };

  // Function to format month and year for display
  const formatMonthYear = (month, year) => {
    return new Date(year, month - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  // Custom tooltip that includes total
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (parseInt(entry.value) || 0), 0);
      
      // Format the date for display
      const displayDate = typeof label === 'string' && label.includes('T')
        ? new Date(label).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
        : label;

      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-sm">{displayDate}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.dataKey}: {parseInt(entry.value) || 0}
            </p>
          ))}
          <p className="font-semibold text-sm mt-2 pt-2 border-t border-gray-200">
            Total: {total}
          </p>
        </div>
      );
    }
    return null;
  };

  // Function to prepare data for daily chart
  const prepareDailyData = () => {
    if (!dailyStats) return [];
    
    // Group by date
    const groupedByDate = dailyStats.reduce((acc, curr) => {
      const date = curr.date;
      if (!acc[date]) {
        acc[date] = { date };
      }
      acc[date][curr.type] = parseInt(curr.count) || 0;
      return acc;
    }, {});

    // Convert to array and sort by date
    return Object.values(groupedByDate)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Function to prepare data for monthly chart
  const prepareMonthlyData = () => {
    if (!monthlyStats) return [];
    
    // Group by month
    const groupedByMonth = monthlyStats.reduce((acc, curr) => {
      if (!acc[curr.month]) {
        acc[curr.month] = { 
          month: formatMonth(curr.month),
          date: new Date(selectedYearMonthly, curr.month - 1, 1)
        };
      }
      acc[curr.month][curr.type] = parseInt(curr.count) || 0;
      return acc;
    }, {});

    // Convert to array and sort by month
    return Object.values(groupedByMonth)
      .sort((a, b) => a.date - b.date)
      .map(({ date, ...rest }) => rest);
  };

  // Define colors for request types
  const typeColors = {
    translate: '#ea4335',  // red
    improve: '#34a853',    // green
    format: '#4285f4',     // blue
    mail: '#fbbc05',       // yellow/orange
    report: '#7f1ac6',      // purple
    lamal: '#61dade',       // cyan
    dictaphone: '#ffb366'   // pastel orange
  };

  // Convert colors array for clinic bars
  const clinicColors = Object.values(typeColors);

  // Function to sort request types by total count
  const getSortedRequestTypes = (stats) => {
    if (!stats || !stats.length) return [];
    
    const typeTotals = stats.reduce((acc, curr) => {
      const type = curr.type;
      const count = parseInt(curr.count) || 0;
      acc[type] = (acc[type] || 0) + count;
      return acc;
    }, {});

    return Object.entries(typeTotals)
      .sort(([, a], [, b]) => b - a)
      .map(([type]) => type);
  };

  // Empty state component
  const EmptyState = ({ message }) => (
    <div className="flex flex-col items-center justify-center h-64 text-gray-500">
      <ChartBarIcon className="w-16 h-16 mb-4" />
      <p className="text-lg">{message}</p>
    </div>
  );

  // Fetch available clinics
  useEffect(() => {
    const fetchClinics = async () => {
      try {
        const data = await apiClient.get('/api/admin/statistics/clinics');
        setClinics(Array.isArray(data.clinics) ? data.clinics : []);
      } catch (err) {
        console.error('Error fetching clinics:', err);
        setClinics([]);
      }
    };

    fetchClinics();
  }, []);

  // Fetch daily statistics
  useEffect(() => {
    const fetchDailyStats = async () => {
      try {
        setIsDailyLoading(true);
        setDailyError(null);
        const clinicParam = selectedClinicDaily === 'all' ? '' : `&clinic_id=${selectedClinicDaily}`;
        const data = await apiClient.get(
          `/api/admin/statistics/daily?month=${selectedMonth}&year=${selectedYear}${clinicParam}`
        );

        setDailyStats(Array.isArray(data.dailyStats) ? data.dailyStats : []);
        setAvailableMonths(Array.isArray(data.availableMonths) ? data.availableMonths : []);
      } catch (err) {
        console.error('Error fetching daily statistics:', err);
        setDailyError('Failed to fetch daily statistics');
        setDailyStats([]);
        setAvailableMonths([]);
      } finally {
        setIsDailyLoading(false);
      }
    };

    fetchDailyStats();
  }, [selectedMonth, selectedYear, selectedClinicDaily]);

  // Fetch monthly statistics
  useEffect(() => {
    const fetchMonthlyStats = async () => {
      try {
        setIsMonthlyLoading(true);
        setMonthlyError(null);
        const clinicParam = selectedClinicMonthly === 'all' ? '' : `&clinic_id=${selectedClinicMonthly}`;
        const data = await apiClient.get(
          `/api/admin/statistics/monthly?year=${selectedYearMonthly}${clinicParam}`
        );

        setMonthlyStats(Array.isArray(data.monthlyStats) ? data.monthlyStats : []);
      } catch (err) {
        console.error('Error fetching monthly statistics:', err);
        setMonthlyError('Impossible de charger les statistiques mensuelles');
        setMonthlyStats([]);
      } finally {
        setIsMonthlyLoading(false);
      }
    };

    fetchMonthlyStats();
  }, [selectedYearMonthly, selectedClinicMonthly]);

  // Fetch clinic-based statistics
  useEffect(() => {
    const fetchClinicStats = async () => {
      if (selectedClinic !== 'all') return;
      
      setIsByClinicLoading(true);
      setByClinicError(null);
      try {
        const data = await apiClient.get(
          `/api/admin/statistics/by-clinic/${selectedYearByClinic}`
        );
        
        setByClinicData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching clinic statistics:', error);
        setByClinicError('Failed to fetch clinic statistics');
        setByClinicData([]);
      } finally {
        setIsByClinicLoading(false);
      }
    };

    fetchClinicStats();
  }, [selectedClinic, selectedYearByClinic]);

  const dailyData = prepareDailyData();
  const monthlyData = prepareMonthlyData();
  const availableYears = [...new Set(availableMonths?.map(({ year }) => year) || [])].sort((a, b) => b - a);
  const sortedRequestTypes = getSortedRequestTypes([...(dailyStats || []), ...(monthlyStats || [])]);

  // Custom tooltip for monthly chart
  const MonthlyTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (parseInt(entry.value) || 0), 0);
      
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-sm">{label}</p>
          {payload.map((entry, index) => ( 
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {parseInt(entry.value) || 0}
            </p>
          ))}
          <p className="font-semibold text-sm mt-2 pt-2 border-t border-gray-200">
            Total: {total}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-semibold">Statistiques des requêtes</h1>
        
        {/* Clinic selector */}
        <Menu as="div" className="relative inline-block text-left">
          {({ open }) => (
            <>
              <Menu.Button className="inline-flex w-56 justify-between items-center rounded-md bg-[rgb(197,214,248)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                {selectedClinic === 'all' ? 'Toutes les cliniques' : clinics.find(c => c.id === selectedClinic)?.name}
                <ChevronDownIcon 
                  className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${
                    open ? 'transform rotate-180' : ''
                  }`} 
                  aria-hidden="true" 
                />
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => setSelectedClinic('all')}
                          className={`${
                            active ? 'bg-[rgb(197,214,248)]' : ''
                          } ${
                            selectedClinic === 'all' ? 'bg-[rgb(197,214,248)]' : ''
                          } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                        >
                          Toutes les cliniques
                        </button>
                      )}
                    </Menu.Item>
                    {clinics.map((clinic) => (
                      <Menu.Item key={clinic.id}>
                        {({ active }) => (
                          <button
                            onClick={() => setSelectedClinic(clinic.id)}
                            className={`${
                              active ? 'bg-[rgb(197,214,248)]' : ''
                            } ${
                              selectedClinic === clinic.id ? 'bg-[rgb(197,214,248)]' : ''
                            } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                          >
                            {clinic.name}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </>
          )}
        </Menu>
      </div>

      {/* Statistics by Clinic - Only visible when "all clinics" is selected */}
      {selectedClinic === 'all' && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-medium">Répartition par clinique</h2>
              
              {/* Year selector for clinic stats */}
              <Menu as="div" className="relative inline-block text-left">
                {({ open }) => (
                  <>
                    <Menu.Button className="inline-flex w-56 justify-between items-center rounded-md bg-[rgb(197,214,248)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                      {selectedYearByClinic}
                      <ChevronDownIcon 
                        className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${
                          open ? 'transform rotate-180' : ''
                        }`} 
                        aria-hidden="true" 
                      />
                    </Menu.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="py-1">
                          {availableYears.map((year) => (
                            <Menu.Item key={year}>
                              {({ active }) => (
                                <button
                                  onClick={() => setSelectedYearByClinic(year)}
                                  className={`${
                                    active ? 'bg-[rgb(197,214,248)]' : ''
                                  } ${
                                    selectedYearByClinic === year ? 'bg-[rgb(197,214,248)]' : ''
                                  } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                                >
                                  {year}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </>
                )}
              </Menu>
            </div>
          </div>

          <div className="p-6">
            {isByClinicLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : byClinicError ? (
              <div className="text-red-600">{byClinicError}</div>
            ) : byClinicData.length === 0 ? (
              <EmptyState message="Aucune statistique par clinique disponible" />
            ) : (
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byClinicData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      tickFormatter={(month) => new Date(2000, month - 1).toLocaleDateString('fr-FR', { month: 'short' })}
                    />
                    <YAxis />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
                              <p className="font-semibold text-sm">
                                {new Date(2000, label - 1).toLocaleDateString('fr-FR', { month: 'long' })}
                              </p>
                              {payload.map((entry, index) => {
                                return (
                                  <p key={index} style={{ color: entry.color }}>
                                    {entry.name}: {entry.value || 0}
                                  </p>
                                );
                              })}
                              <p className="font-semibold text-sm mt-2 pt-2 border-t border-gray-200">
                                Total: {payload.reduce((sum, entry) => sum + (entry.value || 0), 0)}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend 
                      formatter={(value) => {
                        const clinic = clinics.find(c => c.id === value);
                        return clinic?.name || value;
                      }}
                    />
                    {clinics.map((clinic, index) => (
                      <Bar 
                        key={clinic.id}
                        dataKey={(item) => item.clinics[clinic.id] || 0}
                        name={clinic.name}
                        stackId="a"
                        fill={clinicColors[index % clinicColors.length]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Daily Statistics Card */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-medium">Statistiques journalières</h2>
            
            <div className="flex items-center gap-4">
              {/* Clinic selector for daily stats */}
              <Menu as="div" className="relative inline-block text-left">
                {({ open }) => (
                  <>
                    <Menu.Button className="inline-flex w-56 justify-between items-center rounded-md bg-[rgb(197,214,248)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                      {selectedClinicDaily === 'all' ? 'Toutes les cliniques' : clinics.find(c => c.id === selectedClinicDaily)?.name || 'Sélectionner une clinique'}
                      <ChevronDownIcon 
                        className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${
                          open ? 'transform rotate-180' : ''
                        }`} 
                        aria-hidden="true" 
                      />
                    </Menu.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="py-1">
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => setSelectedClinicDaily('all')}
                                className={`${
                                  active ? 'bg-[rgb(197,214,248)]' : ''
                                } ${
                                  selectedClinicDaily === 'all' ? 'bg-[rgb(197,214,248)]' : ''
                                } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                              >
                                Toutes les cliniques
                              </button>
                            )}
                          </Menu.Item>
                          {clinics.map((clinic) => (
                            <Menu.Item key={clinic.id}>
                              {({ active }) => (
                                <button
                                  onClick={() => setSelectedClinicDaily(clinic.id)}
                                  className={`${
                                    active ? 'bg-[rgb(197,214,248)]' : ''
                                  } ${
                                    selectedClinicDaily === clinic.id ? 'bg-[rgb(197,214,248)]' : ''
                                  } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                                >
                                  {clinic.name}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </>
                )}
              </Menu>

              {/* Month/Year selector for daily stats */}
              <Menu as="div" className="relative inline-block text-left">
                {({ open }) => (
                  <>
                    <Menu.Button className="inline-flex w-56 justify-between items-center rounded-md bg-[rgb(197,214,248)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                      {formatMonthYear(selectedMonth, selectedYear)}
                      <ChevronDownIcon 
                        className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${
                          open ? 'transform rotate-180' : ''
                        }`} 
                        aria-hidden="true" 
                      />
                    </Menu.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="py-1">
                          {availableMonths?.sort((a, b) => {
                            // Trier d'abord par année décroissante, puis par mois décroissant
                            if (b.year !== a.year) return b.year - a.year;
                            return b.month - a.month;
                          }).map(({ month, year }) => (
                            <Menu.Item key={`${year}-${month}`}>
                              {({ active }) => (
                                <button
                                  onClick={() => {
                                    setSelectedMonth(month);
                                    setSelectedYear(year);
                                  }}
                                  className={`${
                                    active ? 'bg-[rgb(197,214,248)]' : ''
                                  } ${
                                    selectedMonth === month && selectedYear === year ? 'bg-[rgb(197,214,248)]' : ''
                                  } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                                >
                                  {formatMonthYear(month, year)}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </>
                )}
              </Menu>
            </div>
          </div>
        </div>

        <div className="p-6">
          {isDailyLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : dailyError ? (
            <div className="text-red-600">{dailyError}</div>
          ) : dailyData.length === 0 ? (
            <EmptyState message="Aucune statistique journalière disponible" />
          ) : (
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={formatDate} />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {sortedRequestTypes.map((type) => (
                    <Bar
                      key={type}
                      dataKey={type}
                      stackId="a"
                      fill={typeColors[type]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Monthly Statistics Card */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-medium">Statistiques mensuelles</h2>
            
            <div className="flex items-center gap-4">
              {/* Clinic selector for monthly stats */}
              <Menu as="div" className="relative inline-block text-left">
                {({ open }) => (
                  <>
                    <Menu.Button className="inline-flex w-56 justify-between items-center rounded-md bg-[rgb(197,214,248)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                      {selectedClinicMonthly === 'all' ? 'Toutes les cliniques' : clinics.find(c => c.id === selectedClinicMonthly)?.name || 'Sélectionner une clinique'}
                      <ChevronDownIcon 
                        className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${
                          open ? 'transform rotate-180' : ''
                        }`} 
                        aria-hidden="true" 
                      />
                    </Menu.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="py-1">
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => setSelectedClinicMonthly('all')}
                                className={`${
                                  active ? 'bg-[rgb(197,214,248)]' : ''
                                } ${
                                  selectedClinicMonthly === 'all' ? 'bg-[rgb(197,214,248)]' : ''
                                } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                              >
                                Toutes les cliniques
                              </button>
                            )}
                          </Menu.Item>
                          {clinics.map((clinic) => (
                            <Menu.Item key={clinic.id}>
                              {({ active }) => (
                                <button
                                  onClick={() => setSelectedClinicMonthly(clinic.id)}
                                  className={`${
                                    active ? 'bg-[rgb(197,214,248)]' : ''
                                  } ${
                                    selectedClinicMonthly === clinic.id ? 'bg-[rgb(197,214,248)]' : ''
                                  } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                                >
                                  {clinic.name}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </>
                )}
              </Menu>

              {/* Year selector for monthly stats */}
              <Menu as="div" className="relative inline-block text-left">
                {({ open }) => (
                  <>
                    <Menu.Button className="inline-flex w-56 justify-between items-center rounded-md bg-[rgb(197,214,248)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                      {selectedYearMonthly}
                      <ChevronDownIcon 
                        className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${
                          open ? 'transform rotate-180' : ''
                        }`} 
                        aria-hidden="true" 
                      />
                    </Menu.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="py-1">
                          {availableYears.map((year) => (
                            <Menu.Item key={year}>
                              {({ active }) => (
                                <button
                                  onClick={() => setSelectedYearMonthly(year)}
                                  className={`${
                                    active ? 'bg-[rgb(197,214,248)]' : ''
                                  } ${
                                    selectedYearMonthly === year ? 'bg-[rgb(197,214,248)]' : ''
                                  } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                                >
                                  {year}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </>
                )}
              </Menu>
            </div>
          </div>
        </div>

        <div className="p-6">
          {isMonthlyLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : monthlyError ? (
            <div className="text-red-600">{monthlyError}</div>
          ) : monthlyData.length === 0 ? (
            <EmptyState message="Aucune statistique mensuelle disponible" />
          ) : (
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip content={<MonthlyTooltip />} />
                  <Legend />
                  {sortedRequestTypes.map(type => (
                    <Bar
                      key={type}
                      dataKey={type}
                      stackId="a"
                      fill={typeColors[type]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Statistics; 